/* eslint-disable react/display-name */
import { format } from 'date-fns';
import axios from 'helpers/axios';
import { useCancelToken, useMutableRef, useOpenState, useToasts } from 'hooks';
import _ from 'lodash';
import React from 'react';
import { DataTable, DataTableCell, IconButton } from 'ui/components/shared';
import { getNumberOfPages, recalculatePage } from 'ui/components/shared/DataTable/helpers';
import { ConfirmationModal, Image, Modal, ModalPosition, SuccessModal } from 'ui/components/styled';
import { AddMembers, EditMember } from '../forms';
import { ConfirmDisbaleMember } from '../modals/ConfirmDisableMember';
import { ConfirmRemoveMember } from '../modals/ConfirmRemoveMember';
import { CustomersList } from './CustomersList';

export const MembersList = React.forwardRef(
  ({ company, DrawerCloseButton, preventDeletingLastAdmin = true }, ref) => {
    const [data, setData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [totalRows, setTotalRows] = React.useState(0);
    const [perPage, setPerPage] = React.useState(15);
    const [sort, setSort] = React.useState({ column: '', order: '' });
    const [search, setSearch] = React.useState('');
    const [modal, openModal, closeModal] = useOpenState();
    const [selectedCustomers, setSelectedCustomers] = React.useState({
      customers: [],
      filterCustomers: 'all',
    });
    const { errorToast, removeAllToasts } = useToasts();
    const [currentPage, setCurrentPage] = useMutableRef(1);
    const { newCancelToken, cancelPreviousRequest, isCancel } = useCancelToken();
    const columns = [
      {
        name: 'User name',
        selector: 'user.name',
        cell: (row) => <DataTableCell title={row.user?.name}>{row.user?.name}</DataTableCell>,
        sortable: true,
      },
      {
        name: 'Role',
        selector: 'role',
        sortable: true,
        cell: (row) => (
          <DataTableCell>
            <span>{row.role}</span>
            {row.role === 'admin' && (
              <Image width="9px" mx="xxs" src="/assets/img/icons/star.svg" alt="star" />
            )}
          </DataTableCell>
        ),
        style: {
          textTransform: 'capitalize',
        },
      },
      {
        name: 'Email',
        selector: 'user.email',
        cell: (row) => <DataTableCell title={row.user?.email}>{row.user?.email}</DataTableCell>,
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        cell: (row) => {
          let status, color;
          if (row.enabled) {
            switch (row.status) {
              case 'creator':
                status = 'Creator';
                color = '#3591FC';
                break;
              case 'invite-sent':
                status = 'Invite sent';
                color = '#FCB14A';
                break;
              case 'invite-accepted':
                status = 'Invite accepted';
                color = '#00AA22';
                break;
              case 'invite-declined':
                status = 'Invite declined';
                color = '#F74C4C';
                break;
              case 'invalid-email':
                status = 'Email not valid';
                color = '#8900FF';
                break;
              case 'invite-error':
                status = 'An error occured';
                color = '#F74C4C';
                break;
              default:
                status = _.capitalize(row.status || '');
                break;
            }
          } else {
            status = 'Disabled';
            if (row.disabled_at) {
              status += ` since ${format(new Date(row.disabled_at), 'MM/dd/yyyy HH:mm')}`;
            }
            color = '#F74C4C';
          }

          return (
            status && (
              <DataTableCell title={status} color={color} bold>
                {status}
              </DataTableCell>
            )
          );
        },
        sortable: true,
      },
      {
        name: 'Action',
        right: true,
        button: true,
        ignoreRowClick: true,
        cell: (row) => (
          <>
            {!row.enabled && (
              <IconButton
                title="Re-enable"
                variant="transparent"
                icon="check"
                size="24px"
                mx="xxs"
                onClick={() => openModal('confirm-enable-member', { member: row })}
              />
            )}
            <IconButton
              title="Edit"
              variant="transparent"
              icon="edit"
              mx="xxs"
              onClick={() => openModal('edit-member', { member: row })}
            />
            {preventDeletingLastAdmin &&
            adminMembersCount === 1 &&
            row.role === 'admin' ? null : row.enabled ? (
              <IconButton
                title="Disable"
                variant="transparent"
                icon="disable"
                size="22px"
                mx="xxs"
                onClick={() => openModal('confirm-disable-member', { member: row })}
              />
            ) : (
              <IconButton
                title="Remove"
                variant="transparent"
                icon="trash"
                mx="xxs"
                onClick={() => openModal('confirm-remove-member', { member: row })}
              />
            )}

            <IconButton
              title="Edit list customers to see in the dashboard"
              variant="transparent"
              icon="customer"
              mx="xxs"
              disabled={row?.user?.roles?.includes('admin') || row.role === 'admin'}
              onClick={() => openModal('edit-customers-list', { member: row })}
            />
          </>
        ),
      },
    ];
    const adminMembersCount = React.useMemo(() => {
      const admins = data.filter((member) => member.role === 'admin');
      return admins?.length || 0;
    }, [data]);

    const fetchMembers = async (page) => {
      setLoading(true);

      try {
        const skip = page > 1 ? (page - 1) * perPage : 0;
        const response = await axios.get(
          `/companies/${company?.id}/members/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}`
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotalRows(response.data.count);
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Fetch Members.' });
      }

      setLoading(false);
    };

    const handleRowsPerPageChange = async (newPerPage, page) => {
      setLoading(true);
      setCurrentPage(page);

      try {
        const skip = page > 1 ? (page - 1) * newPerPage : 0;
        const response = await axios.get(
          `/companies/${company?.id}/members/?$top=${newPerPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${search}`
        );

        if (response.status === 200) {
          setData(response.data.value);
          setPerPage(newPerPage);
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Rows Change.' });
      }

      setLoading(false);
    };

    const handlePageChange = (page) => {
      setCurrentPage(page);
      fetchMembers(page);
    };

    const handleSort = async (column, sortDirection) => {
      setLoading(true);

      const sortColumn = column?.selector || '';
      const sortOrder = sortColumn ? sortDirection : '';
      setSort({ column: sortColumn, order: sortOrder });

      try {
        const skip = 0;
        const response = await axios.get(
          `/companies/${company?.id}/members/?$top=${perPage}&$skip=${skip}&$orderby=${sortColumn}&$order=${sortOrder}&$search=${search}`
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotalRows(response.data.count);
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Sort Members.' });
      }

      setLoading(false);
    };

    const handleFilter = async (value) => {
      setLoading(true);
      setSearch(value);

      cancelPreviousRequest();

      try {
        const skip = 0;
        const response = await axios.get(
          `/companies/${company?.id}/members/?$top=${perPage}&$skip=${skip}&$orderby=${sort.column}&$order=${sort.order}&$search=${value}`,
          { cancelToken: newCancelToken() }
        );

        if (response.status === 200) {
          setData(response.data.value);
          setTotalRows(response.data.count);
        }
      } catch (error) {
        if (isCancel(error)) return;
        errorToast({ error, default: 'Error: Filter Members.' });
      }

      setLoading(false);
    };

    const disableMember = async (member, sendEmail = true) => {
      try {
        const response = await axios.post(
          `/companies/${company?.id}/members/${member.id}/disable?sendEmail=${sendEmail}`
        );

        if (response.status === 200) {
          removeAllToasts();
          refresh();
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Disable Member.' });
      }

      closeModal();
    };

    const removeMember = async (member, sendEmail = true) => {
      try {
        const response = await axios.delete(
          `/companies/${company?.id}/members/${member.id}?sendEmail=${sendEmail}`
        );

        if (response.status === 204) {
          removeAllToasts();
          refresh(totalRows - 1);
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Remove Member.' });
      }

      closeModal();
    };

    const enableMember = async (member) => {
      try {
        const response = await axios.post(`/companies/${company?.id}/members/${member.id}/enable`);

        if (response.status === 200) {
          removeAllToasts();
          refresh();
        }
      } catch (error) {
        errorToast({ error, default: 'Error: Enable Member.' });
      }

      closeModal();
    };

    const refresh = (newRowsCount) => {
      if (newRowsCount > 0) {
        const updatedPage = getNumberOfPages(newRowsCount, perPage);
        const recalculatedPage = recalculatePage(currentPage.current, updatedPage);
        if (recalculatedPage !== currentPage.current) {
          setCurrentPage(recalculatedPage);
        }
      }

      fetchMembers(currentPage.current);
    };

    const handleRealtimeUpdates = (eventData) => {
      const updatedData = data?.map((member) => {
        if (member.id === eventData?.id) {
          member.status = eventData?.status || member.status;
        }
        return member;
      });
      setData(updatedData);
    };

    // allow us to call defined functions from parent
    React.useImperativeHandle(ref, () => ({
      handleRealtimeUpdates,
    }));

    React.useEffect(() => {
      fetchMembers(1);
    }, []);

    return (
      <>
        <DataTable
          columns={columns}
          data={data}
          progressPending={loading}
          paginationPerPage={perPage}
          paginationTotalRows={totalRows}
          paginationCurrentPage={currentPage.current}
          onRowsPerPageChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          onPageReset={setCurrentPage}
          onSort={handleSort}
          filterPlaceholder="Members Search"
          onFilter={handleFilter}
          subHeaderTitle={company?.name}
          subHeaderCloseButton={DrawerCloseButton}
          noPadding
        />
        <IconButton
          variant="rounded"
          icon="plus-white"
          position="fixed"
          right="2em"
          bottom="2em"
          onClick={() => openModal('add-member')}
        ></IconButton>
        <Modal
          show={modal.isOpen('add-member', 'edit-member')}
          onClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          top="25%"
          width="60%"
          maxWidth="650px"
        >
          {modal.isOpen('add-member') && (
            <AddMembers
              company={company}
              onClose={closeModal}
              onSuccess={() => {
                refresh();
                openModal('add-member-success');
              }}
            />
          )}
          {modal.isOpen('edit-member') && (
            <EditMember member={modal.params?.member} onClose={closeModal} onSuccess={refresh} />
          )}
        </Modal>
        <Modal
          show={modal.isOpen('edit-customers-list')}
          onClose={closeModal}
          position={ModalPosition.TOP_CENTER}
          top="10%"
          width="60%"
          maxWidth="650px"
        >
          {modal.isOpen('edit-customers-list') && (
            <CustomersList
              selectedCustomers={selectedCustomers}
              setSelectedCustomers={setSelectedCustomers}
              company={company}
              member={modal.params?.member}
              refresh={refresh}
              closeModal={closeModal}
            />
          )}
        </Modal>
        <ConfirmDisbaleMember
          show={modal.isOpen('confirm-disable-member')}
          onClose={closeModal}
          onConfirm={disableMember}
          params={modal.params}
        />
        <ConfirmRemoveMember
          show={modal.isOpen('confirm-remove-member')}
          onClose={closeModal}
          onConfirm={removeMember}
          params={modal.params}
        />

        <ConfirmationModal
          show={modal.isOpen('confirm-enable-member')}
          onClose={closeModal}
          onConfirm={() => enableMember(modal.params?.member)}
        >
          Are you sure you want to re-enable member &quot;
          {modal.params?.member?.user?.name}&quot; ?
        </ConfirmationModal>
        <SuccessModal show={modal.isOpen('add-member-success')} onClose={closeModal}>
          Congrats the users were successfully added!
        </SuccessModal>
      </>
    );
  }
);
